<template>
  <div v-if="news.length > 0" class="news-home">
    <article class="news-home__lead">
      <router-link :to="postLink(lead)" class="news-home__lead-link">
        <span class="news-home__lead-frame">
          <img :src="lead.url" :alt="lead.title" class="news-home__lead-image" />
        </span>
        <p class="news-home__meta">
          <span class="news-home__category">{{ lead.category }}</span>
          <time>{{ lead.date }}</time>
        </p>
        <h3 class="news-home__lead-title">{{ lead.title }}</h3>
      </router-link>
    </article>

    <div class="news-home__more">
      <div class="news-home__more-bar">
        <router-link to="/news" class="text-link">Ver todas as notícias</router-link>
      </div>
      <ul class="news-home__list">
        <li v-for="item in rest" :key="item.id">
        <article>
          <router-link :to="postLink(item)" class="news-home__item">
            <img :src="item.url" alt="" class="news-home__thumb" loading="lazy" />
            <div class="news-home__item-body">
              <p class="news-home__meta">
                <span class="news-home__category">{{ item.category }}</span>
                <time>{{ item.date }}</time>
              </p>
              <h3 class="news-home__title">{{ item.title }}</h3>
            </div>
          </router-link>
        </article>
      </li>
      </ul>
    </div>
  </div>

  <div v-else class="news-home news-home--loading" aria-busy="true">
    <span class="visually-hidden">Carregando notícias</span>
    <div class="news-home__placeholder news-home__placeholder--lead" aria-hidden="true"></div>
    <div class="news-home__list" aria-hidden="true">
      <div v-for="n in 3" :key="n" class="news-home__placeholder"></div>
    </div>
  </div>
</template>

<script>
import newsService from "../../../service/newsService";

export default {
  name: "NewsHome",
  data() {
    return {
      news: [],
    };
  },
  computed: {
    lead() {
      return this.news[0];
    },
    rest() {
      return this.news.slice(1, 4);
    },
  },
  methods: {
    postLink(item) {
      return { path: `/post/news/${item.id}` };
    },
    getNews() {
      newsService.get({ limit: 4 }).then((res) => {
        this.news = res.data.map((card) => ({
          id: card.id,
          title: card.title,
          category: card.category,
          date: new Date(card.created_at).toLocaleDateString("pt-BR"),
          url: card.path_img_card,
        }));
      });
    },
  },
  mounted() {
    this.getNews();
  },
};
</script>

<style lang="scss" scoped>
// Destaque em largura total; as três notícias seguintes em cards na mesma linha.
.news-home {
  display: flex;
  flex-direction: column;
  gap: 48px;

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 0 0 10px;
    font-size: var(--fs-meta);
    line-height: 1.4;
    color: var(--c-muted);

    time {
      font-variant-numeric: tabular-nums;
    }
  }

  &__category {
    font-weight: 600;
    color: var(--c-navy-deep);
  }

  &__lead-link {
    display: block;
    color: inherit;
    text-decoration: none;

    &:hover .news-home__lead-title {
      text-decoration-color: currentColor;
    }

    &:hover .news-home__lead-image {
      transform: scale(1.015);
    }
  }

  &__lead-frame {
    display: block;
    border-radius: var(--radius);
    overflow: hidden;
    background: #e6e9ee;
  }

  &__lead-image {
    display: block;
    width: 100%;
    height: clamp(260px, 36vw, 420px);
    object-fit: cover;
    transition: transform 400ms var(--ease);
  }

  &__lead-title {
    margin: 22px 0 0;
    max-width: 30em;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: var(--fs-h2);
    line-height: 1.12;
    letter-spacing: -0.015em;
    color: var(--c-navy);
    text-wrap: balance;
    text-decoration: none;
    transition: color 160ms var(--ease);
  }

  &__lead-link:hover .news-home__lead-title {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 6px;
  }

  // Divisão entre o destaque e o restante: título próprio e filete acima da grade.
  &__more {
    margin-top: 8px;
    padding-top: 24px;
    border-top: 1px solid var(--c-line);
  }

  &__more-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 24px;
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  // Cada card ocupa a altura da sua linha do grid, para os três ficarem da mesma altura.
  &__list > li {
    display: flex;
  }

  &__list > li > article {
    display: flex;
    flex: 1;
  }

  &__item {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    overflow: hidden;
    background: var(--c-surface);
    border: 1px solid var(--c-line);
    border-radius: var(--radius);
    color: inherit;
    text-decoration: none;
    transition: transform 240ms var(--ease), box-shadow 240ms var(--ease), border-color 240ms var(--ease);

    &:hover {
      border-color: #c5ccd8;
      transform: translateY(-4px);
      box-shadow: 0 14px 28px rgba(51, 70, 102, 0.12);
    }

    &:hover .news-home__title {
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 5px;
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px 20px 22px;
  }

  &__thumb {
    display: block;
    width: 100%;
    aspect-ratio: calc(3 / 2);
    object-fit: contain;
    background: #eef2f7;
    transition: transform 400ms var(--ease);
  }

  &__item:hover &__thumb {
    transform: scale(1.03);
  }

  // Respeita quem prefere menos movimento: mantém só a mudança de borda.
  @media (prefers-reduced-motion: reduce) {
    &__item,
    &__item:hover,
    &__thumb,
    &__item:hover &__thumb {
      transition: border-color 160ms var(--ease);
      transform: none;
    }
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: var(--fs-h4);
    line-height: 1.25;
    color: var(--c-navy);
    text-decoration: underline;
    text-decoration-color: transparent;
    text-decoration-thickness: 1px;
    text-underline-offset: 5px;
    transition: text-decoration-color 160ms var(--ease);
  }

  // Esqueleto discreto enquanto a API responde (evita salto de layout).
  &__placeholder {
    height: 180px;
    border-radius: var(--radius);
    background: #e9ecf0;
  }

  &__placeholder--lead {
    height: auto;
    aspect-ratio: calc(21 / 9);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  @media (max-width: 959px) {
    &__list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 599px) {
    gap: 36px;

    &__lead-image {
      aspect-ratio: calc(4 / 3);
    }

    &__list {
      grid-template-columns: 1fr;
      gap: 28px;
    }
  }
}
</style>
